<script lang="ts">
    import * as Popover from "../ui/popover/index.js";
    import * as Command from "../ui/command/index.js";
    import Button from "../ui/button/button.svelte";
    import ChevronsUpDownIcon from "@lucide/svelte/icons/chevrons-up-down";
    import { tick } from "svelte";
    import type { WithChild } from "bits-ui";

    let {
        value = $bindable(),
        triggerClass,
        child,
        placeholder,
        onSelect,
        ...props
    }: WithChild<{
        value: string | null;
        items: Array<{ id: string; text: string }>;
        autofocus?: boolean;
        triggerClass?: string;
        placeholder?: string;
        onSelect?: (x: string) => void;
    }> = $props();

    let open = $state(props.autofocus ?? false);
    let triggerRef = $state<HTMLButtonElement | null>(null);

    const selectedValue = $derived(props.items.find((x) => x.id === value)?.text ?? "");

    // We want to refocus the trigger button when the user selects
    // an item from the list so users can continue navigating the
    // rest of the form with the keyboard.
    function closeAndFocusTrigger() {
        open = false;
        tick().then(() => {
            triggerRef?.focus();
        });
    }
</script>

<Popover.Root bind:open>
    <Popover.Trigger bind:ref={triggerRef}>
        {#snippet child({ props })}
            {#if child}
                {@render child({ props })}
            {:else}
                <Button
                    variant="outline"
                    role="combobox"
                    {...props}
                    class={["flex w-full justify-between", triggerClass]}
                >
                    {#if selectedValue}
                        <div>{selectedValue}</div>
                    {:else}
                        <div class="text-muted-foreground">{placeholder}</div>
                    {/if}
                    <ChevronsUpDownIcon class="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
            {/if}
        {/snippet}
    </Popover.Trigger>
    <Popover.Content class="w-50 p-0">
        <Command.Root>
            <Command.Input placeholder="" />
            <Command.List>
                <Command.Empty></Command.Empty>
                <Command.Group>
                    {#each props.items as item (item.id)}
                        <Command.Item
                            value={item.id}
                            keywords={[item.text]}
                            onSelect={() => {
                                value = item.id;
                                closeAndFocusTrigger();
                                onSelect?.(item.id);
                            }}
                        >
                            {item.text}
                        </Command.Item>
                    {/each}
                </Command.Group>
            </Command.List>
        </Command.Root>
    </Popover.Content>
</Popover.Root>
